import { Component, ViewEncapsulation, OnInit, OnDestroy, ViewContainerRef, ChangeDetectorRef, AfterViewInit, Renderer2, Inject, DOCUMENT } from "@angular/core";
import {
    Router,
    Event as RouterEvent,
    NavigationStart,
    NavigationEnd,
    NavigationCancel,
    NavigationError
} from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ToastrService } from "ngx-toastr";
import { AuthService } from "../../services/auth.service";
import { BusinessFunctionService } from "../../services/businessfunction.service";
import { ConfigurationService } from "../../services/configuration.service";
import { DisclaimerService } from "../../services/disclaimer.service";
import { Title } from "@angular/platform-browser";
import { RoutesService } from "../../services/routes.service";
import { environment } from "../../../environments/environment";

@Component({
    selector: "app",
    encapsulation: ViewEncapsulation.None,
    templateUrl: "./app.component.html",
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
    private destroy$ = new Subject<void>();

    // sets initial value to true to show loading spinner on first load
    isLoading: boolean = true;
    disclaimerNotAccepted: boolean = false;
    disclaimerAllowedOnPage: boolean = false;
    mainClasses = "";

    _satellite: any;
    currentUser: any;
    pageData: any;
    AdobeAnalyticsUrl: string = "https://assets.adobedtm.com/58d4dfc81ba0/3949c56bd33e/launch-bc9390270269.min.js";
    //AdobeAnalyticsUrl:string = Configuration.GetSection("AppSettings").GetSection("AzureAd");

    constructor(private router: Router,
        private authService: AuthService,
        private businessFunctionService: BusinessFunctionService,
        vRef: ViewContainerRef,
        public toastr: ToastrService,
        private configurationService: ConfigurationService,
        private changeDetectionRef: ChangeDetectorRef,
        private renderer2: Renderer2,
        @Inject(DOCUMENT) private document: Document,
        private routesService: RoutesService,
        private titleService: Title,
        private disclaimerService: DisclaimerService) {
        // Note: ngx-toastr doesn't require setRootViewContainerRef

        // sets initial value to true to show loading spinner on first load
        router.events
            .pipe(takeUntil(this.destroy$))
            .subscribe((event: RouterEvent) => {
                this.toggleLoading(event);
            });
    }

    /**
     * get all business functions on load, these are used in various places throughout the app
     */
    ngOnInit(): void {
        this.businessFunctionService.getAll()
            .pipe(takeUntil(this.destroy$))
            .subscribe(bfs => this.authService.businessFunctions = bfs);

        this.authService.accessTokenExpiredAnnounced$
            .pipe(takeUntil(this.destroy$))
            .subscribe((expired: boolean) => {
                if (expired) {
                    if (this.authService.businessFunction) {
                        if (this.authService.businessFunction.authenticationRequired) {
                            this.router.navigate([`${this.authService.businessFunction.defaultRoute}/dashboard`]);
                        }
                    }
                }
            });

        this.authService.businessFunctionAnnounced$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.disclaimerNotAccepted = AuthService.businessFunction
                    ? !this.disclaimerService.getConsentForBusinessFunction(AuthService.businessFunction.defaultRoute)
                    : false;
            });

        this.router.events
            .pipe(takeUntil(this.destroy$))
            .subscribe(val => {
                if (val instanceof NavigationStart) {
                    if (val.url === "/businessfunctionselector") {
                        this.mainClasses = "bg-black h-100 mt-0";
                    } else {
                        this.mainClasses = "";
                    }
                }
            });
    }

    ngAfterViewInit() {
        this.appendAdobeAnalyticsCode();
        this.isCookieLoaded();
    }

    /**
     * Loads OneTrust cookie consent SDK based on environment
     */
    isCookieLoaded() {
        const appUrl = document.location.href;
        const isProdOrStage = appUrl.includes('stage.taxcms.deloitte.com') || 
                              (appUrl.includes('taxcms.deloitte.com') && !appUrl.includes('dev.') && !appUrl.includes('qa.'));
        
        const scriptId = isProdOrStage 
            ? environment.oneTrust.domainScriptId 
            : `${environment.oneTrust.domainScriptId}-test`;
        
        const script = document.createElement("script");
        script.setAttribute("src", environment.oneTrust.scriptUrl);
        script.setAttribute("data-document-language", "true");
        script.setAttribute("type", "text/javascript");
        script.setAttribute("charset", "UTF-8");
        script.setAttribute("data-domain-script", scriptId);
        document.getElementsByTagName("head")[0].appendChild(script);
        
        // Define OptanonWrapper globally
        if (!(window as any).OptanonWrapper) {
            (window as any).OptanonWrapper = function() { };
        }
    }

     /**
     * checks to see if the user has a business function selected
     */
    loggedIn(): boolean {
        return AuthService.businessFunctionId != null;
    }

    /**
     * toggles the loading image based on the router event
     * @param event
     */
    toggleLoading(event: RouterEvent): void {
        if (event instanceof NavigationStart) {
            this.isLoading = true;

            // determine if we need to show the disclaimer or not
            const e: NavigationStart = event as NavigationStart;
            this.showDisclaimer(e);
        }
        if (event instanceof NavigationEnd) {
            var production: boolean = false;
            this.isLoading = false;
            window.scrollTo(0, 0);
            this.updateBrowserTitle();
            try {
                this.setupAdobeAnalyticsGlobalObject(event);
                this._satellite = window['_satellite'];
                this._satellite.track("page_load");
            } catch (error) {
            }
        }

        // set loading state to false in both of the below events to hide the spinner in case a request fails
        if (event instanceof NavigationCancel) {
            this.isLoading = false;
        }
        if (event instanceof NavigationError) {
            this.isLoading = false;
        }
        this.changeDetectionRef.detectChanges();
    }

    /**
     * Determines if the disclaimer should be shown or not
     * @param event the event of the router
     */
    //Adobe analytics code 
    showDisclaimer(event: NavigationStart): void {
        if (event.url === "/splash" || event.url === "/businessfunctionselector" || event.url.indexOf("logon") > -1) {
            this.disclaimerAllowedOnPage = false;
        } else {
            this.disclaimerAllowedOnPage = true;
        }
        this.disclaimerNotAccepted = AuthService.businessFunction ? !this.disclaimerService.getConsentForBusinessFunction(AuthService.businessFunction.defaultRoute) : false;
    }

    /**
     * Sets the browser tab title to "PortalName | PageName" using the active
     * business function's display name and the current route's nav text.
     */
    private updateBrowserTitle(): void {
        try {
            let route: any = this.router.routerState.snapshot.root;
            while (route && route.firstChild) {
                route = route.firstChild;
            }
            let navText: string = route && route.data && route.data['nav'] && route.data['nav'].text
                ? route.data['nav'].text
                : '';

            if (!navText && route && route.routeConfig) {
                const staticSegments = route.routeConfig.path
                    .split('/')
                    .filter((s: string) => !s.startsWith(':'));

                for (const config of this.router.config) {
                    if (config.data && config.data['nav'] && config.data['nav'].text) {
                        const configStaticSegments = (config.path || '')
                            .split('/')
                            .filter((s: string) => !s.startsWith(':'));
                        if (configStaticSegments.length > 0 &&
                            configStaticSegments.every((seg: string, i: number) => seg === staticSegments[i])) {
                            navText = config.data['nav'].text;
                            break;
                        }
                    }
                }
            }

            const portalName: string = AuthService.businessFunction && AuthService.businessFunction.displayName
                ? AuthService.businessFunction.displayName
                : '';

            let title: string = '';
            if (portalName && navText) {
                title = `${portalName} | ${navText}`;
            } else if (portalName) {
                title = portalName;
            } else if (navText) {
                title = navText;
            }

            if (title) {
                this.titleService.setTitle(title);
            }
        } catch (error) {
        }
    }

    private appendAdobeAnalyticsCode() {
        let adobeUrlToBeLoaded = this.AdobeAnalyticsUrl;
        try {
            //const script = document.createElement("script");
            let script = this.renderer2.createElement('script');
            script.type = 'text/plain';
            script.classList = 'optanon-category-2';
            script.src = adobeUrlToBeLoaded;
            //script.async = true;
            //const head = document.getElementsByTagName("head")[0];
            //head.insertBefore(script, head.firstChild);
            //this.renderer2.appendChild(this._document.body, script);   
            this.renderer2.appendChild(this.document.head, script);
        } catch (ex) {
        }
    }
    private setupAdobeAnalyticsGlobalObject(event: NavigationEnd): void {
        let pageName: string = "Unknown";
        let businessFunctionName: string;
        this.routesService.currentPageTitle
            .pipe(takeUntil(this.destroy$))
            .subscribe(res => {
                pageName = res;
            });
        let userData: { userName: string | undefined; userRole: string | undefined } = {
            userName: undefined,
            userRole: undefined
        };
        if (event.url === "/splash") {
            userData.userName = undefined;
            userData.userRole = undefined;
        }
        else if (sessionStorage['currentClientUser']) {
            userData.userName = JSON.parse(sessionStorage['currentClientUser']).name;
            userData.userRole = JSON.parse(sessionStorage['currentClientUser']).roles[0].name;
        }

        //let userEmail = AuthService && AuthService.user.email  ? AuthService.user.email : undefined;
        //    let userRole = AuthService && sessionStorage.currentClientUser ? JSON.parse(sessionStorage.currentClientUser).roles[0].name : undefined;
        //     if(userEmail)
        //     {
        //         userData.userEmail =  userEmail;
        //     }   
        //     if(userRole)
        //     {
        //         userData.userRole = userRole;
        //     }
        window.digitalData = {
            page: {
                pageInfo: {
                    pageName: pageName,
                    siteSection: "SiteSelection"
                }
            },
            user: userData

        };



    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
